import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { forwardRef, useState, useRef, useEffect } from 'react';
import typography from 'storybookConfig/mixins/typography';
import Icon from 'storybookConfig/stories/molecules/Icon';

import Flex from '@react-css/flex';

export interface SearchableChipSelectorOption {
  label: string;
  value: string;
}

export interface SearchableChipSelectorProps {
  /**
   * Array of options to choose from
   */
  options: SearchableChipSelectorOption[];
  /**
   * Array of currently selected values
   */
  value: string[];
  /**
   * Callback when selection changes
   */
  onChange: (values: string[]) => void;
  /**
   * Placeholder text for the search input
   */
  placeholder?: string;
  /**
   * Whether the component is disabled
   */
  disabled?: boolean;
  /**
   * Additional CSS class name
   */
  className?: string;
  /**
   * onFocus callback
   */
  onFocus?: () => void;
  /**
   * Maximum number of options to show in dropdown (default: 50)
   */
  maxDisplayOptions?: number;
}

const Container = styled.div`
  position: relative;
  width: 100%;
`;

const SearchInputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input<{ disabled?: boolean }>`
  width: 100%;
  height: 46px;
  border: 1px solid ${({ theme }) => theme.color.gray300};
  border-radius: 23px;
  padding: 10px 16px 10px 48px;
  outline: none;
  ${typography('Inputs/Input')};
  background-color: ${({ theme }) => theme.color.white};

  &::placeholder {
    color: ${({ theme }) => theme.color.bodyTextSecondary};
  }

  &:focus {
    border-color: ${({ theme }) => theme.color.blue400};
  }

  ${({ disabled, theme }) =>
    disabled &&
    css`
      cursor: not-allowed;
      background-color: ${theme.color.gray100};
      color: ${theme.color.bodyTextDisabled};
    `}
`;

const SearchIcon = styled(Icon)`
  position: absolute;
  left: 16px;
  pointer-events: none;
  color: ${({ theme }) => theme.color.bodyTextSecondary};
`;

const DropdownContainer = styled.div<{ isVisible: boolean }>`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 1000;
  background: white;
  border: 1px solid ${({ theme }) => theme.color.gray300};
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  max-height: 200px;
  overflow-y: auto;
  margin-top: 4px;

  ${({ isVisible }) =>
    !isVisible &&
    css`
      display: none;
    `}
`;

const DropdownItem = styled.button<{ isSelected: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: none;
  text-align: left;
  cursor: pointer;
  ${typography('Body/Regular')};
  color: ${({ theme }) => theme.color.bodyTextPrimary};

  &:hover {
    background-color: ${({ theme }) => theme.color.gray100};
  }

  &:focus {
    background-color: ${({ theme }) => theme.color.blue100};
    outline: none;
  }

  ${({ isSelected, theme }) =>
    isSelected &&
    css`
      background-color: ${theme.color.blue100};
      color: ${theme.color.blue600};
    `}
`;

const ChipsContainer = styled(Flex)`
  margin-top: 12px;
  flex-wrap: wrap;
  gap: 8px;
`;

const NoResultsText = styled.div`
  padding: 12px 16px;
  color: ${({ theme }) => theme.color.bodyTextSecondary};
  ${typography('Body/Regular')};
  text-align: center;
`;

const MoreOptionsText = styled.div`
  padding: 8px 16px;
  color: ${({ theme }) => theme.color.bodyTextSecondary};
  ${typography('Body/Body Small')};
  text-align: center;
  border-top: 1px solid ${({ theme }) => theme.color.gray200};
  background-color: ${({ theme }) => theme.color.gray100};
  font-style: italic;
`;

const RemovableChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background-color: ${({ theme }) => theme.color.blue050};
  border: 1px solid ${({ theme }) => theme.color.blue200};
  border-radius: 20px;
  ${typography('Chips/Chip Text Large')};
  color: ${({ theme }) => theme.color.bodyTextPrimary};
`;

const RemoveButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  color: ${({ theme }) => theme.color.bodyTextSecondary};

  &:hover {
    color: ${({ theme }) => theme.color.bodyTextPrimary};
  }

  &:focus {
    outline: 2px solid ${({ theme }) => theme.color.blue400};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

/**
 * A searchable chip selector component that allows users to search through options
 * and add them as removable chips. Perfect for category selection, tags, etc.
 */
const SearchableChipSelector = forwardRef<HTMLDivElement, SearchableChipSelectorProps>(
  (
    {
      options,
      value,
      onChange,
      placeholder = 'Search and add...',
      disabled,
      className,
      onFocus,
      maxDisplayOptions = 50,
    },
    ref
  ) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Filter options based on search term and exclude already selected items
    const availableOptions = options.filter((option) => !value.includes(option.value));

    const filteredOptions = searchTerm
      ? availableOptions.filter((option) =>
          option.label.toLowerCase().includes(searchTerm.toLowerCase())
        )
      : availableOptions;

    // Limit displayed options to prevent overwhelming UI
    const displayedOptions = filteredOptions.slice(0, maxDisplayOptions);

    // Close dropdown when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setIsDropdownOpen(false);
          setFocusedIndex(-1);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newSearchTerm = e.target.value;
      setSearchTerm(newSearchTerm);
      setIsDropdownOpen(true); // Always show dropdown when typing
      setFocusedIndex(-1);
    };

    const handleInputFocus = () => {
      setIsDropdownOpen(true); // Show dropdown on focus, even without search term
      onFocus?.();
    };

    const handleOptionSelect = (option: SearchableChipSelectorOption) => {
      if (!disabled) {
        onChange([...value, option.value]);
        setSearchTerm('');
        setIsDropdownOpen(false);
        setFocusedIndex(-1);
        inputRef.current?.focus();
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
      if (!isDropdownOpen || displayedOptions.length === 0) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => (prev < displayedOptions.length - 1 ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case 'Enter':
          e.preventDefault();
          if (focusedIndex >= 0) {
            handleOptionSelect(displayedOptions[focusedIndex]);
          }
          break;
        case 'Escape':
          setIsDropdownOpen(false);
          setFocusedIndex(-1);
          break;
        default:
          // No action needed for other keys
          break;
      }
    };

    const handleChipRemove = (valueToRemove: string) => {
      if (!disabled) {
        onChange(value.filter((v) => v !== valueToRemove));
      }
    };

    // Get label for a value
    const getLabelForValue = (val: string) => {
      return options.find((option) => option.value === val)?.label || val;
    };

    return (
      <Container ref={ref} className={className}>
        <div ref={containerRef}>
          <SearchInputContainer>
            <SearchIcon name="search" size="20px" />
            <SearchInput
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onKeyDown={handleKeyDown}
              placeholder={placeholder}
              disabled={disabled}
            />
          </SearchInputContainer>

          <DropdownContainer isVisible={isDropdownOpen && displayedOptions.length > 0}>
            {displayedOptions.map((option, index) => (
              <DropdownItem
                key={option.value}
                isSelected={index === focusedIndex}
                onClick={() => handleOptionSelect(option)}
              >
                {option.label}
              </DropdownItem>
            ))}
            {filteredOptions.length > maxDisplayOptions && (
              <MoreOptionsText>
                +{filteredOptions.length - maxDisplayOptions} more options available - keep typing
                to search
              </MoreOptionsText>
            )}
          </DropdownContainer>

          {isDropdownOpen && displayedOptions.length === 0 && searchTerm && (
            <DropdownContainer isVisible>
              <NoResultsText>No results found</NoResultsText>
            </DropdownContainer>
          )}
        </div>

        {value.length > 0 && (
          <ChipsContainer>
            {value.map((val) => (
              <RemovableChip key={val}>
                {getLabelForValue(val)}
                {!disabled && (
                  <RemoveButton onClick={() => handleChipRemove(val)}>
                    <Icon name="close" size="16px" />
                  </RemoveButton>
                )}
              </RemovableChip>
            ))}
          </ChipsContainer>
        )}
      </Container>
    );
  }
);

SearchableChipSelector.displayName = 'SearchableChipSelector';

export default SearchableChipSelector;
